import { useTranslation } from 'react-i18next';
import { howItWorksSteps } from '@/mocks/howItWorks';

interface Track {
  id: string;
  icon: string;
  titleKey: string;
  descKey: string;
  numbers: number[];
  accent: 'warm' | 'ink';
}

const tracks: Track[] = [
  {
    id: 'planning',
    icon: 'ri-search-eye-line',
    titleKey: 'overview.track1.title',
    descKey: 'overview.track1.desc',
    numbers: [1, 2, 3, 4, 5, 6, 7],
    accent: 'warm',
  },
  {
    id: 'production',
    icon: 'ri-cpu-line',
    titleKey: 'overview.track2.title',
    descKey: 'overview.track2.desc',
    numbers: [8, 9, 10, 11, 12, 13],
    accent: 'ink',
  },
];

export default function ProcessOverview() {
  const { t } = useTranslation('howItWorks');

  return (
    <section id="overview" className="w-full bg-white py-14 md:py-20">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <span className="text-xs font-semibold text-warm-500 uppercase tracking-[0.2em] mb-3 block">
              {t('overview.label')}
            </span>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-ink-700 mb-4">
              {t('overview.title')}
            </h2>
            <p className="text-sm md:text-base text-ink-400 max-w-2xl mx-auto leading-relaxed">
              {t('overview.subtitle')}
            </p>
            <div className="w-16 h-1 bg-warm-500 mx-auto mt-6 rounded-full" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6">
            {tracks.map((track) => {
              const steps = howItWorksSteps.filter((s) => track.numbers.includes(s.number));

              return (
                <div key={track.id} className="p-6 md:p-8 bg-white border border-ink-100 rounded-xl">
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        track.accent === 'warm' ? 'bg-warm-50' : 'bg-ink-50'
                      }`}
                    >
                      <i
                        className={`${track.icon} text-xl md:text-2xl ${
                          track.accent === 'warm' ? 'text-warm-500' : 'text-ink-600'
                        }`}
                      />
                    </div>
                    <h3 className="text-lg md:text-xl font-bold text-ink-700">{t(track.titleKey)}</h3>
                  </div>
                  <p className="text-sm text-ink-400 leading-relaxed mb-6">{t(track.descKey)}</p>

                  <div className="flex flex-wrap items-center gap-2">
                    {steps.map((step, i) => (
                      <div key={step.id} className="flex items-center gap-2">
                        <a
                          href={`#step-${step.number}`}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs md:text-sm font-medium whitespace-nowrap transition-colors ${
                            track.accent === 'warm'
                              ? 'bg-warm-50 text-warm-700 hover:bg-warm-100'
                              : 'bg-ink-50 text-ink-700 hover:bg-ink-100'
                          }`}
                        >
                          <span
                            className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${
                              track.accent === 'warm' ? 'bg-warm-500' : 'bg-ink-600'
                            }`}
                          >
                            {step.number}
                          </span>
                          {t(`steps.${step.number}.short`)}
                        </a>
                        {i < steps.length - 1 && (
                          <i
                            className={`ri-arrow-right-line text-xs ${
                              track.accent === 'warm' ? 'text-warm-400' : 'text-ink-300'
                            }`}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}